import { useMemo } from 'react';

import { useMemberId } from '@trello/authentication';
import { sortBoards } from '@trello/boards';
import { useMemberInboxIds } from '@trello/personal-workspace';
import { useRecentBoards } from '@trello/recent-boards';
import { useWorkspace } from '@trello/workspaces';

import { useBoardSwitcherMemberFragment } from './BoardSwitcherMemberFragment.generated';
import { useBoardSwitcherWorkspaceFragment } from './BoardSwitcherWorkspaceFragment.generated';
import { isRealWorkspace } from './isRealWorkspace';

export const useBoardSwitcher = () => {
  const memberId = useMemberId();
  const { workspaceId } = useWorkspace();

  const { idBoard: inboxBoardId, idOrganization: personalWorkspaceId } =
    useMemberInboxIds();

  const { data: recentData, loading: recentLoading } = useRecentBoards();

  const recentBoards = recentData?.boards ?? [];

  const { data } = useBoardSwitcherMemberFragment({
    from: { id: memberId },
    returnPartialData: true,
  });

  const organizations = data?.organizations ?? [];

  const fragmentId =
    isRealWorkspace(workspaceId) && workspaceId !== personalWorkspaceId
      ? workspaceId
      : null;

  const { data: currentWorkspace } = useBoardSwitcherWorkspaceFragment({
    from: { id: fragmentId },
  });

  const remainingWorkspaces = organizations
    .filter(
      (workspace) =>
        workspace.id !== workspaceId && workspace.id !== personalWorkspaceId,
    )
    .sort((a, b) => {
      const aName = a.displayName ?? a.name ?? '';
      const bName = b.displayName ?? b.name ?? '';
      return aName.localeCompare(bName);
    });

  const workspaces = currentWorkspace?.id
    ? [currentWorkspace, ...remainingWorkspaces]
    : [...remainingWorkspaces];

  const workspaceIds = workspaces.map((workspace) => workspace.id);

  const boardStars = data?.boardStars ?? [];
  const filteredBoardStars = boardStars.filter(({ idBoard }) =>
    data?.boards?.some(({ id }) => id === idBoard),
  );
  const starredBoards = [...filteredBoardStars].sort((a, b) => {
    return a.pos - b.pos;
  });

  const workspaceBoards = useMemo(() => {
    const map = new Map<string, string[]>();

    const myStarredBoardIds = starredBoards
      .map(({ idBoard }) => idBoard)
      .filter((idBoard) => idBoard !== inboxBoardId);

    const sortedBoards = sortBoards({
      boards: data?.boards ?? [],
      myStarredBoardIds,
    });

    sortedBoards.forEach(({ id, idOrganization }) => {
      if (id === inboxBoardId) {
        return;
      }

      if (!idOrganization || idOrganization === personalWorkspaceId) {
        return;
      }

      const boards = map.get(idOrganization) ?? [];
      map.set(idOrganization, [...boards, id]);
    });

    return map;
  }, [data?.boards, inboxBoardId, personalWorkspaceId, starredBoards]);

  const guestWorkspaceIds = useMemo(() => {
    // key: workspaceId, value: workspace displayName
    const map = new Map<string, string>();

    const boards = data?.boards ?? [];

    boards.forEach(({ idOrganization, organization }) => {
      if (
        idOrganization &&
        !workspaceIds.includes(idOrganization) &&
        idOrganization !== personalWorkspaceId &&
        organization?.displayName
      ) {
        map.set(idOrganization, organization?.displayName);
      }
    });

    return Array.from(map.keys()).sort((a, b) => {
      const aName = map.get(a) ?? '';
      const bName = map.get(b) ?? '';
      return aName.localeCompare(bName);
    });
  }, [data?.boards, personalWorkspaceId, workspaceIds]);

  return {
    currentWorkspace,
    recentBoards,
    recentLoading,
    starredBoards,
    workspaces,
    workspaceBoards,
    guestWorkspaceIds,
  };
};
